"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from "react";

import { cx } from "../../lib/cx";
import { ISOLOGO_SRC } from "../../lib/marca";
import { Icon, type IconName } from "../core/Icon";
import s from "./SidebarNav.module.css";

export interface SidebarNavItem {
  value: string;
  label: string;
  icon: IconName;
  /** Lo pendiente. Expandida es una píldora violeta; contraída, un punto sobre el icono. */
  count?: number;
  /** Con `href` el ítem es un enlace (se navega con `as`, p. ej. `Link`). Sin él, un botón que llama `onChange`. */
  href?: string;
}

export interface SidebarNavSection {
  id: string;
  /** "Carpetas", "Integraciones". */
  label: string;
  items: SidebarNavItem[];
  /** Arranca abierta. Default `true`. */
  defaultOpen?: boolean;
  /** Con la barra contraída la sección sigue mostrando sus ítems bajo una regla (Carpetas). Sin esto desaparece (Integraciones). */
  pinned?: boolean;
  /** El "+" del encabezado. Sin callback no se dibuja: un botón que no hace nada es peor que ninguno. */
  onAdd?: () => void;
  addLabel?: string;
  /** El "⋯" del encabezado. Ídem. */
  onMore?: () => void;
  moreLabel?: string;
}

export interface SidebarNavSearch {
  /** La ruta del listado que recibe la búsqueda por GET. */
  action?: string;
  /** Abre una command palette en lugar de un formulario GET. */
  onOpen?: () => void;
  /** El nombre del parámetro. Default `"buscar"`. */
  name?: string;
  placeholder?: string;
}

/** `white` es la barra blanca sobre la página gris; `gray` la invierte (barra gray-100, campos blancos). */
export type SidebarNavTone = "white" | "gray";

export interface SidebarNavProps extends Omit<ComponentPropsWithoutRef<"aside">, "onChange"> {
  items?: SidebarNavItem[];
  value?: string;
  onChange?: (value: string) => void;
  /** El pie: la cuenta. Puede leer `useSidebarNav()` para achicarse con la barra. En celular no se muestra. */
  footer?: ReactNode;
  brand?: string;
  /** Reemplaza al isologo de Klavia por otra imagen (cuadrada, 28 px). */
  logoSrc?: string;
  /** Componente para los ítems con `href`. Default `"a"`. */
  as?: ElementType;
  /** Secciones secundarias debajo de la navegación principal, plegables. */
  sections?: SidebarNavSection[];
  /** El campo "Buscar" con ⌘K. Sin él, no hay búsqueda. */
  search?: SidebarNavSearch;
  tone?: SidebarNavTone;
  /** Controlado. */
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const SidebarNavContext = createContext<{ collapsed: boolean }>({ collapsed: false });

/** Lo que un hijo de la barra (el pie, la cuenta) necesita saber de ella. Fuera de una `SidebarNav` dice "expandida". */
export function useSidebarNav() {
  return useContext(SidebarNavContext);
}

/** La barra lateral del panel: 248 px contraíble a 68. En celular se vuelve una barra inferior con los ítems principales. */
export function SidebarNav({
  items = [],
  value,
  onChange,
  footer,
  brand = "Klavia",
  logoSrc,
  as,
  sections = [],
  search,
  tone = "white",
  collapsed: collapsedProp,
  defaultCollapsed = false,
  onCollapsedChange,
  className,
  ...rest
}: SidebarNavProps) {
  const LinkTag: ElementType = as ?? "a";
  const [collapsedInterno, setCollapsedInterno] = useState(defaultCollapsed);
  const collapsed = collapsedProp ?? collapsedInterno;
  const setCollapsed = (siguiente: boolean) => {
    if (collapsedProp === undefined) setCollapsedInterno(siguiente);
    onCollapsedChange?.(siguiente);
  };

  const [abiertas, setAbiertas] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(sections.map((sec) => [sec.id, sec.defaultOpen ?? true])),
  );

  // ⌘K / Ctrl+K enfoca la búsqueda; si la barra está contraída, primero la
  // expande y enfoca cuando el campo existe.
  const campo = useRef<HTMLInputElement>(null);
  const [enfocar, setEnfocar] = useState(false);
  const abrirBusqueda = () => {
    if (search?.onOpen) { search.onOpen(); return; }
    if (collapsed) {
      setCollapsed(false);
      setEnfocar(true);
    } else {
      campo.current?.focus();
    }
  };
  useEffect(() => {
    if (enfocar && !collapsed) {
      campo.current?.focus();
      setEnfocar(false);
    }
  }, [enfocar, collapsed]);
  useEffect(() => {
    if (!search || search.onOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        abrirBusqueda();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const renderItem = (it: SidebarNavItem) => {
    const activo = it.value === value;
    const contenido = (
      <>
        <span className={s.icono}>
          <Icon name={it.icon} size={18} />
          {it.count !== undefined ? <span className={s.punto} /> : null}
        </span>
        <span className={s.label}>{it.label}</span>
        {it.count !== undefined ? <span className={s.contador}>{it.count}</span> : null}
        <span className={s.tip} aria-hidden="true">
          {it.label}
        </span>
      </>
    );
    return (
      <li key={it.value}>
        {it.href ? (
          <LinkTag href={it.href} className={s.item} aria-current={activo ? "page" : undefined}>
            {contenido}
          </LinkTag>
        ) : (
          <button type="button" className={s.item} aria-current={activo ? "page" : undefined} onClick={() => onChange?.(it.value)}>
            {contenido}
          </button>
        )}
      </li>
    );
  };

  return (
    <aside className={cx(s.nav, className)} data-collapsed={collapsed} data-tone={tone} {...rest}>
      <div className={s.cabecera}>
        {logoSrc ? (
          <img src={logoSrc} alt="" className={s.logoImg} />
        ) : (
          <img src={ISOLOGO_SRC} alt="" className={s.isologo} aria-hidden="true" />
        )}
        <span className={s.wordmark}>{brand}</span>
        <button
          type="button"
          className={s.toggle}
          aria-label={collapsed ? "Expandir menú" : "Contraer menú"}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(!collapsed)}
        >
          <Icon name={collapsed ? "panel-left-open" : "panel-left-close"} size={16} />
        </button>
      </div>

      {search ? (
        search.onOpen ? <div className={s.buscador}>
          <button type="button" className={cx(s.busqueda, s.disparador)} aria-label="Buscar" aria-haspopup="dialog" aria-keyshortcuts="Meta+K Control+K" onClick={search.onOpen}>
            <Icon name="search" size={15} />
            <span className={s.textoBusqueda}>{search.placeholder ?? "Buscar"}</span>
            <kbd className={s.atajo}>⌘K</kbd>
          </button>
          <button type="button" className={s.busquedaContraida} aria-label="Buscar" aria-haspopup="dialog" onClick={search.onOpen}><Icon name="search" size={16} /></button>
        </div> : <form className={s.buscador} action={search.action} method="get" role="search">
          <label className={s.busqueda}>
            <Icon name="search" size={15} />
            <input
              ref={campo}
              type="search"
              name={search.name ?? "buscar"}
              placeholder={search.placeholder ?? "Buscar"}
              aria-label={search.placeholder ?? "Buscar"}
              autoComplete="off"
              className={s.campo}
            />
            <kbd className={s.atajo}>⌘K</kbd>
          </label>
          <button type="button" className={s.busquedaContraida} aria-label="Buscar" onClick={abrirBusqueda}>
            <Icon name="search" size={16} />
          </button>
        </form>
      ) : null}

      <nav aria-label="Principal">
        <ul className={s.items}>
          {items.map(renderItem)}
          {search?.onOpen ? <li className={s.busquedaMovil}><button type="button" className={s.item} aria-label="Buscar" aria-haspopup="dialog" onClick={search.onOpen}><span className={s.icono}><Icon name="search" size={18} /></span><span className={s.label}>Buscar</span></button></li> : null}
        </ul>
      </nav>

      {sections.map((sec) => {
        const abierta = abiertas[sec.id] ?? true;
        const mostrarLista = abierta || (collapsed && sec.pinned);
        return (
          <div key={sec.id} className={s.seccion} data-pinned={sec.pinned || undefined}>
            <span className={s.regla} />
            <div className={s.seccionCabecera}>
              <button
                type="button"
                className={s.accion}
                aria-label={abierta ? `Ocultar ${sec.label.toLowerCase()}` : `Mostrar ${sec.label.toLowerCase()}`}
                aria-expanded={abierta}
                onClick={() => setAbiertas((prev) => ({ ...prev, [sec.id]: !abierta }))}
              >
                <Icon name={abierta ? "chevron-down" : "chevron-right"} size={14} />
              </button>
              <span className={s.seccionTitulo}>{sec.label}</span>
              {sec.onAdd ? (
                <button type="button" className={s.accion} aria-label={sec.addLabel ?? "Agregar"} onClick={sec.onAdd}>
                  <Icon name="plus" size={14} />
                </button>
              ) : null}
              {sec.onMore ? (
                <button type="button" className={s.accion} aria-label={sec.moreLabel ?? "Más opciones"} onClick={sec.onMore}>
                  <Icon name="ellipsis-vertical" size={14} />
                </button>
              ) : null}
            </div>
            {mostrarLista ? (
              <nav aria-label={sec.label}>
                <ul className={s.items}>{sec.items.map(renderItem)}</ul>
              </nav>
            ) : null}
          </div>
        );
      })}

      {footer ? (
        <div className={s.pie}>
          <SidebarNavContext.Provider value={{ collapsed }}>{footer}</SidebarNavContext.Provider>
        </div>
      ) : null}
    </aside>
  );
}
